import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { readWorkbook } from "@/office/excel/workbook";
import { MAKRO_SZENARIEN } from "../makros";

export const lektionSzenarien: Lesson = {
  id: "szenarien",
  title: "Der Szenario-Manager",
  summary: "Bester Fall, schlechtester Fall, Planfall – nebeneinander, ohne die Mappe dreimal zu kopieren.",
  durationMinutes: 11,
  learningGoals: [
    "Szenarien mit verschiedenen Eingabewerten anlegen",
    "Zwischen Szenarien umschalten",
    "Einen Szenariobericht erstellen",
  ],
  steps: [
    {
      id: "szenarien-anlegen",
      title: "Szenarien anlegen",
      explanation: [
        "Ein Szenario ist ein benannter Satz von Eingabewerten. „Daten“ → „Was-wäre-wenn-" +
          "Analyse“ → „Szenario-Manager“ → „Hinzufügen“: Name vergeben („Optimistisch“), die " +
          "veränderbaren Zellen markieren (etwa Zins und Laufzeit), dann die Werte eintragen.",
        "Mit „Anzeigen“ schreibt Excel die Werte eines Szenarios in die Zellen – das Modell " +
          "rechnet sofort neu. So lassen sich Varianten vorführen, ohne Zahlen von Hand zu " +
          "ändern und hinterher den Ausgangsstand zu suchen.",
        "Wichtig: Der Szenario-Manager merkt sich nur Werte. Die veränderbaren Zellen " +
          "sollten deshalb reine Eingaben sein, keine Formeln – beim Anzeigen würden Formeln " +
          "überschrieben.",
      ],
      keyPoints: [
        "Daten → Was-wäre-wenn-Analyse → Szenario-Manager → Hinzufügen.",
        "Veränderbare Zellen = Eingaben, keine Formeln.",
        "„Anzeigen“ schreibt die Werte in die Zellen.",
      ],
      task: {
        instruction:
          "Lege für dein Kreditmodell mindestens zwei Szenarien an, etwa „Optimistisch“ und " +
          "„Pessimistisch“ mit unterschiedlichen Zinssätzen.",
        hint: "Szenario-Manager → Hinzufügen → Name, veränderbare Zelle(n) → Werte eingeben → noch einmal Hinzufügen.",
        macro: MAKRO_SZENARIEN,
        check: () =>
          pruefeMitMakro(MAKRO_SZENARIEN, (bericht) => {
            const szenarien = [...bericht.gruppe("szenario").values()];
            return fromCriteria(
              [
                criterion(
                  `Mindestens zwei Szenarien (gefunden: ${szenarien.map((s) => s.get("name")).join(", ") || "keine"})`,
                  szenarien.length >= 2,
                ),
              ],
              {
                success: "Die Varianten sind gespeichert – ein Klick auf „Anzeigen“ wechselt zwischen ihnen.",
                failure: "Es gibt noch nicht genug Szenarien.",
                hint: "Szenarien gehören zu dem Blatt, das beim Anlegen aktiv war.",
              },
            );
          }),
      },
    },
    {
      id: "bericht",
      title: "Der Szenariobericht",
      explanation: [
        "„Zusammenfassung“ im Szenario-Manager erzeugt ein neues Blatt „Szenariobericht“, " +
          "das alle Szenarien nebeneinanderstellt: oben die veränderbaren Zellen, darunter " +
          "die Ergebniszellen, die du angibst – etwa die Monatsrate.",
        "Der Bericht enthält feste Werte, keine Verknüpfung. Ändert sich das Modell, wird " +
          "er neu erzeugt. Tipp: Vorher den Eingabe- und Ergebniszellen Namen geben (Modul " +
          "„Namen“) – dann steht im Bericht „Zins“ statt „$B$2“.",
      ],
      keyPoints: [
        "Szenario-Manager → Zusammenfassung → Ergebniszellen angeben.",
        "Der Bericht ist eine Momentaufnahme.",
        "Benannte Zellen machen den Bericht lesbar.",
      ],
      task: {
        instruction: "Erstelle über „Zusammenfassung“ einen Szenariobericht mit der Monatsrate als Ergebniszelle.",
        hint: "Szenario-Manager → Zusammenfassung → Berichtstyp „Szenariobericht“ → Ergebniszelle B4.",
        check: async () => {
          const mappe = await readWorkbook();
          const bericht = mappe.sheets.find((sheet) => /^(Szenariobericht|Scenario Summary)/i.test(sheet.name));
          return fromCriteria([criterion("Ein Blatt „Szenariobericht“ ist vorhanden", bericht !== undefined)], {
            success: "Alle Varianten auf einen Blick – bereit für die Besprechung.",
            failure: "Es gibt noch kein Blatt „Szenariobericht“.",
          });
        },
      },
    },
  ],
};
